import {
  type Accessor,
  type ParentComponent,
  createContext,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
  useContext,
} from 'solid-js';
import { useQueryClient } from '@tanstack/solid-query';
import { holds } from '~/shared/lib/permission-implications';
import { followSessionTransitions } from '@sportpark/shared/session-transition';
import { startRealtime, stopRealtime } from './signalr';
import {
  keepSessionFresh,
  readSession,
  subscribeSession,
  writeSession,
  type StoredSession,
} from './http-client';
import { isImpersonationToken } from './auth-api';

export type { StoredSession } from './http-client';
export type AuthSession = StoredSession;

interface AuthContextValue {
  session: Accessor<AuthSession | null>;
  isAuthenticated: Accessor<boolean>;
  isImpersonating: Accessor<boolean>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: readonly string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  signIn: (session: AuthSession) => void;
  signOut: () => void;
  updateTenant: (tenant: { tenantId: string; tenantName: string }) => void;
  renameUser: (name: { firstName: string | null; lastName: string | null }) => void;
}

const AuthContext = createContext<AuthContextValue>();

export const AuthProvider: ParentComponent = (props) => {
  const queryClient = useQueryClient();
  const [session, setSession] = createSignal<AuthSession | null>(readSession());

  const isAuthenticated = createMemo(() => session() !== null);
  const isImpersonating = createMemo(() => {
    const token = session()?.accessToken;
    return token ? isImpersonationToken(token) : false;
  });

  const hasRole = (role: string): boolean => session()?.user.roles.includes(role) ?? false;
  const hasAnyRole = (roles: readonly string[]): boolean => {
    const current = session()?.user.roles ?? [];
    return roles.some((r) => current.includes(r));
  };
  const hasPermission = (permission: string): boolean => holds(session()?.user.permissions ?? [], permission);

  const signIn = (next: AuthSession): void => {
    writeSession(next);
  };
  const signOut = (): void => {
    writeSession(null);
  };
  const updateUser = (patch: Partial<AuthSession['user']>): void => {
    const curr = readSession();
    if (curr) writeSession({ ...curr, user: { ...curr.user, ...patch } });
  };
  const updateTenant = (tenant: { tenantId: string; tenantName: string }): void => {
    updateUser(tenant);
  };
  const renameUser = (name: { firstName: string | null; lastName: string | null }): void => {
    updateUser(name);
  };

  const stopFollowing = followSessionTransitions(subscribeSession, queryClient, {
    stopActivity: () => void stopRealtime(),
    show: (next) => {
      setSession(() => next);
    },
    startActivity: () => void startRealtime(),
  });

  onMount(() => {
    if (session()) void startRealtime();
    const stopKeeping = keepSessionFresh();
    onCleanup(stopKeeping);
  });
  onCleanup(stopFollowing);

  const value: AuthContextValue = {
    session,
    isAuthenticated,
    isImpersonating,
    hasRole,
    hasAnyRole,
    hasPermission,
    signIn,
    signOut,
    updateTenant,
    renameUser,
  };

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
};
